import { CheckCircleFilled, InfoCircleFilled } from '@ant-design/icons';
import { Button, Dropdown, Spin, Tooltip, notification } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import DownSvg from 'assets/svg/Down';
import { useSaveCurrentView } from 'components/molecules/PricingSimulatorV3/useSaveCurrentView';
import { useSavedViewDelete } from 'components/molecules/PricingSimulatorV3/useSavedViewDelete';
import { colorConstants } from 'config/colorConstants';
import {
    fetchPpaViewMetadata,
    PPA_VIEW_LIST_QUERY_KEY,
    usePpaViewMutations,
} from 'hooks/ppa/usePpaViews';
import useFetchViewList from 'hooks/ppa/useFetchViewList';
import {
    applyOptimisticDefaultView,
    applyOptimisticScenarioViewSelection,
    clearOptimisticViewListSelection,
    clearScenarioWorkspaceCache,
    resolveWorkspaceViewSelection,
    restoreViewListCache,
    seedScenarioViewMetadataCache,
} from 'hooks/ppa/ppaViewPrefetch.helpers';
import { fetchScenarioViewMetadata, getPpaViewMetadataQueryKey } from 'hooks/ppa/useFetchViewMetadata';
import type { PPAViewDataSource, PPAViewListItem } from 'types/ppaViews';
import { getAuthUserMail, sendNotification } from 'utils/helperMethods';
import {
    buildUpdateViewPayloadFromMetadata,
    resolvePpaViewApiErrorMessage,
} from 'utils/ppaHelpers';

import SavedViewDropdownPanel from './components/SavedViewDropdownPanel';
import SaveCurrentViewForm from './components/SaveCurrentViewForm';
import SavedViewsListDropdown from './components/SavedViewsListDropdown';
import {
    MAX_USER_CREATED_VIEWS,
    SAVE_VIEW_TOOLTIP_PROPS,
    UPDATED_VIEW_MODIFIED_TOOLTIP,
} from './savedViewStateSelect.constants';
import {
    getViewNameValidationError,
    initialSaveViewFormState,
    normalizeAppliedScope,
} from './savedViewStateSelect.helpers';
import type {
    SaveViewFormState,
    SavedViewActionLoading,
    SavedViewListLoadingAction,
    SavedViewStateSelectProps,
} from './SavedViewStateSelect.types';

const SavedViewStateSelect = ({
    scenarioId,
    ppaScopeId = null,
    appliedScope = [],
    isScenarioModified = false,
    isScenarioOwner = false,
    getLiveViewConfig,
    onViewLoaded,
    onSelectedViewChange,
    onLoadedViewChange,
    onViewListLoadingChange,
    onUpdateLoadedView,
    onPersistScenarioSnapshot,
    onRequestUnsavedLeave,
    onExitViewRequested,
    onScenarioExitComplete,
}: SavedViewStateSelectProps) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [dropdownViewMode, setDropdownViewMode] = useState<'list' | 'save'>('list');
    const [saveViewForm, setSaveViewForm] = useState<SaveViewFormState>(initialSaveViewFormState);
    const [workspaceViewId, setWorkspaceViewId] = useState<number | null>(null);
    const [activeDataSource, setActiveDataSource] = useState<PPAViewDataSource>('view');
    const [expandedViewId, setExpandedViewId] = useState<number | null>(null);
    const [actionLoading, setActionLoading] = useState<SavedViewActionLoading>(null);

    const startViewActionLoading = useCallback((viewId: number, action: SavedViewListLoadingAction) => {
        setActionLoading({ viewId, action });
    }, []);

    const clearViewActionLoading = useCallback(() => {
        setActionLoading(null);
    }, []);
    const [hasNoViewSelected, setHasNoViewSelected] = useState(false);
    const bootstrapViewSelectionRef = useRef<number | null>(null);

    const queryClient = useQueryClient();
    const userId = getAuthUserMail() || null;
    const {
        data: viewListData,
        isLoading: isViewListLoading,
        isFetching: isViewListFetching,
        isPending: isViewListPending,
        isError: isViewListError,
        refetch,
    } = useFetchViewList({
        scenarioId: scenarioId ?? null,
        ppaScopeId,
        alwaysFresh: true,
    });
    const { createViewMutation, deleteViewMutation, setDefaultViewMutation } = usePpaViewMutations();

    const savedViews: PPAViewListItem[] = useMemo(
        () => (viewListData?.views ?? []).filter((view) => view.data_source !== 'scenario'),
        [viewListData?.views],
    );
    const userCreatedViewsCount = useMemo(
        () => savedViews.filter((view) => view.view_type === 'CUSTOM').length,
        [savedViews],
    );

    const activeViewId = activeDataSource === 'view' ? workspaceViewId : null;
    const isViewModified = Boolean(isScenarioModified && activeViewId != null && isScenarioOwner);
    const showModifiedIndicator = Boolean(isScenarioModified && activeViewId != null);

    const selectedViewLabel = useMemo(() => {
        if (activeDataSource === 'scenario') {
            return '—';
        }
        if (activeViewId == null) {
            return '—';
        }
        return savedViews.find((view) => view.view_id === activeViewId)?.view_name ?? '—';
    }, [activeDataSource, activeViewId, savedViews]);

    const viewLabelContent = useMemo(
        () => (
            <span className="flex min-w-0 w-full items-center overflow-hidden text-left text-[13px] !text-black !hover:text-black">
                <span className="shrink-0 opacity-50">View: </span>
                {showModifiedIndicator ? (
                    <span className="flex min-w-0 flex-1 items-center justify-start overflow-hidden">
                        <span className="min-w-0 shrink overflow-hidden whitespace-nowrap">{selectedViewLabel}</span>
                        <span className="shrink-0">*</span>
                    </span>
                ) : (
                    <span className="min-w-0 flex-1 truncate">{selectedViewLabel}</span>
                )}
            </span>
        ),
        [selectedViewLabel, showModifiedIndicator],
    );

    useEffect(() => {
        setWorkspaceViewId(null);
        setActiveDataSource('view');
        setExpandedViewId(null);
        setHasNoViewSelected(false);
        bootstrapViewSelectionRef.current = null;

        if (scenarioId == null) {
            return;
        }

        clearScenarioWorkspaceCache(queryClient, scenarioId, userId, {
            includeViewList: false,
            ppaScopeId,
        });
    }, [queryClient, scenarioId, userId, ppaScopeId]);

    useEffect(() => {
        onViewListLoadingChange?.(isViewListPending);
    }, [isViewListPending, onViewListLoadingChange, scenarioId]);

    useEffect(() => {
        if (
            isViewListPending ||
            isViewListFetching ||
            !viewListData ||
            scenarioId == null ||
            isScenarioModified ||
            hasNoViewSelected
        ) {
            return;
        }

        if (bootstrapViewSelectionRef.current === scenarioId) {
            return;
        }

        bootstrapViewSelectionRef.current = scenarioId;

        void (async () => {
            try {
                const selection = await resolveWorkspaceViewSelection(queryClient, {
                    scenarioId,
                    viewListData,
                    userId,
                    isScenarioOwner,
                });

                if (selection.viewId == null) {
                    return;
                }

                await queryClient.fetchQuery({
                    queryKey: getPpaViewMetadataQueryKey(
                        selection.viewId,
                        scenarioId,
                        selection.dataSource,
                        userId,
                    ),
                    queryFn: () =>
                        fetchScenarioViewMetadata(selection.viewId as number, {
                            scenarioId,
                            dataSource: selection.dataSource,
                        }),
                    staleTime: 0,
                });

                setActiveDataSource(selection.dataSource);
                setWorkspaceViewId(selection.viewId);
            } catch (error) {
                bootstrapViewSelectionRef.current = null;
                sendNotification(
                    'error',
                    resolvePpaViewApiErrorMessage(error, 'Failed to load view. Please try again.'),
                );
            }
        })();
    }, [
        hasNoViewSelected,
        isScenarioModified,
        isScenarioOwner,
        isViewListFetching,
        isViewListPending,
        queryClient,
        scenarioId,
        userId,
        viewListData,
    ]);

    useEffect(() => {
        if (isViewListPending || hasNoViewSelected || workspaceViewId == null) {
            return;
        }

        onSelectedViewChange?.({ viewId: workspaceViewId, dataSource: activeDataSource });
    }, [activeDataSource, hasNoViewSelected, isViewListPending, onSelectedViewChange, workspaceViewId]);

    useEffect(() => {
        if (activeViewId == null || activeDataSource !== 'view') {
            onLoadedViewChange?.(null, false, undefined);
            return;
        }
        const activeView = savedViews.find((view) => view.view_id === activeViewId);
        onLoadedViewChange?.(activeViewId, activeView?.view_type === 'CUSTOM', activeView?.view_name);
    }, [activeDataSource, activeViewId, onLoadedViewChange, savedViews]);

    const resetSaveViewForm = useCallback(() => {
        setSaveViewForm(initialSaveViewFormState);
    }, []);

    const resolveTemplateViewId = useCallback((): number | null => {
        if (workspaceViewId != null) {
            return workspaceViewId;
        }
        const defaultView = savedViews.find((view) => view.is_default);
        return defaultView?.view_id ?? savedViews[0]?.view_id ?? null;
    }, [savedViews, workspaceViewId]);

    const showCreateViewNotification = useCallback((viewName: string, wasLoaded: boolean) => {
        notification.success({
            message: 'New View created',
            description: wasLoaded
                ? `View "${viewName}" is created successfully and has been loaded.`
                : `View "${viewName}" is created successfully.`,
            icon: <CheckCircleFilled style={{ color: '#52C41A', fontSize: 20 }} />,
            placement: 'topRight',
            style: {
                width: 420,
                borderRadius: 8,
                padding: '20px 24px',
            },
        });
    }, []);

    const showViewDeletedNotification = useCallback((viewName: string) => {
        notification.success({
            message: `View "${viewName}" deleted`,
            icon: (
                <InfoCircleFilled
                    style={{ position: 'relative', top: -3, color: colorConstants.purpleFont, fontSize: 20 }}
                />
            ),
            placement: 'topRight',
            duration: 5,
            style: {
                width: 420,
                borderRadius: 8,
                padding: '20px 24px',
            },
        });
    }, []);

    const showViewRenamedNotification = useCallback(() => {
        notification.success({
            message: 'View name updated successfully',
            icon: <CheckCircleFilled style={{ color: '#52C41A', fontSize: 20 }} />,
            placement: 'topRight',
            duration: 5,
            style: {
                width: 420,
                borderRadius: 8,
                padding: '20px 24px',
            },
        });
    }, []);

    const showViewSetAsDefaultNotification = useCallback((viewName: string) => {
        notification.success({
            message: 'View set as default',
            description: `View '${viewName}' has been set as default successfully`,
            icon: <CheckCircleFilled style={{ color: '#52C41A', fontSize: 20 }} />,
            placement: 'topRight',
            duration: 5,
            style: {
                width: 420,
                borderRadius: 8,
                padding: '20px 24px',
            },
        });
    }, []);

    const normalizedAppliedScope = useMemo(() => normalizeAppliedScope(appliedScope), [appliedScope]);

    const { activateAndLoadView, handleDeleteView } = useSavedViewDelete({
        scenarioId,
        ppaScopeId,
        userId,
        activeViewId,
        expandedViewId,
        isScenarioOwner,
        queryClient,
        deleteViewMutation,
        startViewActionLoading,
        clearViewActionLoading,
        setExpandedViewId,
        setHasNoViewSelected,
        setWorkspaceViewId,
        setActiveDataSource,
        onSelectedViewChange,
        onLoadedViewChange,
        onViewLoaded,
        showViewDeletedNotification,
    });

    const { saveCurrentView } = useSaveCurrentView({
        scenarioId,
        ppaScopeId,
        userId,
        workspaceViewId,
        isScenarioOwner,
        appliedScope: normalizedAppliedScope,
        savedViews,
        saveViewForm,
        getLiveViewConfig,
        resolveTemplateViewId,
        getViewNameValidationError,
        createViewMutation,
        queryClient,
        resetSaveViewForm,
        setDropdownViewMode,
        setExpandedViewId,
        setHasNoViewSelected,
        setWorkspaceViewId,
        setActiveDataSource,
        onSelectedViewChange,
        onViewLoaded,
        showCreateViewNotification,
    });

    const completeExitView = useCallback(async () => {
        if (scenarioId == null) {
            return;
        }

        setHasNoViewSelected(true);
        setWorkspaceViewId(null);
        setActiveDataSource('view');
        clearOptimisticViewListSelection(queryClient, scenarioId, userId);
        onSelectedViewChange?.({ viewId: null, dataSource: 'view' });
        onLoadedViewChange?.(null, false, undefined);
        setExpandedViewId(null);
        setIsDropdownOpen(false);
    }, [onLoadedViewChange, onSelectedViewChange, queryClient, scenarioId, userId]);

    const completeExitToScenario = useCallback(
        async (baseViewId: number) => {
            if (scenarioId == null) {
                return;
            }

            const liveConfig = getLiveViewConfig?.() ?? null;
            if (liveConfig != null) {
                seedScenarioViewMetadataCache(queryClient, {
                    scenarioId,
                    userId,
                    baseViewId,
                    liveConfig,
                });
            } else {
                void queryClient.invalidateQueries({
                    queryKey: getPpaViewMetadataQueryKey(baseViewId, scenarioId, 'scenario', userId),
                });
            }

            applyOptimisticScenarioViewSelection(
                queryClient,
                scenarioId,
                baseViewId,
                userId,
                ppaScopeId,
            );
            void queryClient.invalidateQueries({ queryKey: [PPA_VIEW_LIST_QUERY_KEY] });

            setHasNoViewSelected(false);
            setWorkspaceViewId(baseViewId);
            setActiveDataSource('scenario');
            onSelectedViewChange?.({ viewId: baseViewId, dataSource: 'scenario' });
            onLoadedViewChange?.(null, false, undefined);
            onScenarioExitComplete?.();
            setExpandedViewId(null);
            setIsDropdownOpen(false);
        },
        [
            getLiveViewConfig,
            onLoadedViewChange,
            onScenarioExitComplete,
            onSelectedViewChange,
            ppaScopeId,
            queryClient,
            scenarioId,
            userId,
        ],
    );

    const handleExitView = useCallback(
        (view: PPAViewListItem) => {
            if (activeViewId !== view.view_id || scenarioId == null) {
                return;
            }

            const runOwnerExit = async () => {
                startViewActionLoading(view.view_id, 'exit');
                try {
                    await onPersistScenarioSnapshot?.();
                    await completeExitToScenario(view.view_id);
                } catch (error) {
                    sendNotification(
                        'error',
                        resolvePpaViewApiErrorMessage(error, 'Failed to exit view. Please try again.'),
                    );
                } finally {
                    clearViewActionLoading();
                }
            };

            const runReaderExit = async () => {
                startViewActionLoading(view.view_id, 'exit');
                try {
                    await completeExitView();
                } finally {
                    clearViewActionLoading();
                }
            };

            if (onPersistScenarioSnapshot) {
                // Owner: when the view has unsaved changes, exit must behave like every
                // other leave path (scenario tab switch, navigation) and surface the
                // Unsaved View popup so the user can choose Update / Cancel / Discard.
                // The chosen action persists to the scenario; afterwards we land on the
                // "—" scenario view. With no unsaved changes, exit silently.
                if (isViewModified && onRequestUnsavedLeave) {
                    setIsDropdownOpen(false);
                    onRequestUnsavedLeave(() => void completeExitToScenario(view.view_id));
                    return;
                }

                void runOwnerExit();
                return;
            }

            if (onExitViewRequested) {
                onExitViewRequested(() => void runReaderExit());
                return;
            }

            void runReaderExit();
        },
        [
            activeViewId,
            completeExitToScenario,
            completeExitView,
            isViewModified,
            onExitViewRequested,
            onPersistScenarioSnapshot,
            onRequestUnsavedLeave,
            scenarioId,
            startViewActionLoading,
            clearViewActionLoading,
        ],
    );

    const loadViewIntoWorkspace = useCallback(
        async (view: PPAViewListItem) => {
            if (scenarioId == null) {
                return;
            }

            startViewActionLoading(view.view_id, 'load');
            try {
                await activateAndLoadView(view.view_id);
                sendNotification('success', `"${view.view_name}" loaded`);
                setIsDropdownOpen(false);
            } catch (error) {
                sendNotification(
                    'error',
                    resolvePpaViewApiErrorMessage(error, 'Failed to load view. Please try again.'),
                );
            } finally {
                clearViewActionLoading();
            }
        },
        [activateAndLoadView, clearViewActionLoading, scenarioId, startViewActionLoading],
    );

    const handleLoadView = useCallback(
        (view: PPAViewListItem) => {
            if (scenarioId == null) {
                return;
            }

            const loadAfterPersistingScenarioIfNeeded = async () => {
                if (!isViewModified && isScenarioModified && onPersistScenarioSnapshot) {
                    await onPersistScenarioSnapshot();
                }
                await loadViewIntoWorkspace(view);
            };

            // Owner with unsaved changes in the current loaded view: route through
            // the Unsaved View popup first.
            if (isViewModified && onRequestUnsavedLeave) {
                setIsDropdownOpen(false);
                onRequestUnsavedLeave(() => void loadAfterPersistingScenarioIfNeeded());
                return;
            }

            void loadAfterPersistingScenarioIfNeeded();
        },
        [
            isScenarioModified,
            isViewModified,
            loadViewIntoWorkspace,
            onPersistScenarioSnapshot,
            onRequestUnsavedLeave,
            scenarioId,
        ],
    );

    const handleUpdateLoadedView = useCallback(
        async (view: PPAViewListItem) => {
            if (!isScenarioOwner || !onUpdateLoadedView || activeViewId !== view.view_id) {
                return;
            }

            startViewActionLoading(view.view_id, 'update');
            try {
                await onUpdateLoadedView();
                await refetch();
                sendNotification('success', `"${view.view_name}" updated`);
                setIsDropdownOpen(false);
            } catch (error) {
                sendNotification(
                    'error',
                    resolvePpaViewApiErrorMessage(error, 'Failed to update view. Please try again.'),
                );
            } finally {
                clearViewActionLoading();
            }
        },
        [activeViewId, clearViewActionLoading, isScenarioOwner, onUpdateLoadedView, refetch, startViewActionLoading],
    );

    const handleRenameView = useCallback(
        async (view: PPAViewListItem, viewName: string): Promise<boolean> => {
            const trimmedName = viewName.trim();
            const validationError = getViewNameValidationError(
                viewName,
                savedViews.map((item) => item.view_name),
                view.view_name,
            );
            if (!trimmedName || trimmedName === view.view_name.trim() || validationError) {
                return false;
            }

            startViewActionLoading(view.view_id, 'rename');
            try {
                const metadata = await fetchPpaViewMetadata(view.view_id);
                await createViewMutation.mutateAsync(
                    buildUpdateViewPayloadFromMetadata(metadata, metadata.updated_at, {
                        view_name: trimmedName,
                        metadataOnly: true,
                    }),
                );
                void queryClient.invalidateQueries({ queryKey: [PPA_VIEW_LIST_QUERY_KEY] });
                showViewRenamedNotification();
                return true;
            } catch (error) {
                sendNotification(
                    'error',
                    resolvePpaViewApiErrorMessage(error, 'Failed to rename view. Please try again.'),
                );
                return false;
            } finally {
                clearViewActionLoading();
            }
        },
        [
            clearViewActionLoading,
            createViewMutation,
            queryClient,
            savedViews,
            showViewRenamedNotification,
            startViewActionLoading,
        ],
    );

    const handleSetDefaultView = useCallback(
        async (view: PPAViewListItem, checked: boolean) => {
            if (!checked || view.is_default || scenarioId == null) {
                return;
            }

            const previousList = applyOptimisticDefaultView(
                queryClient,
                scenarioId,
                view.view_id,
                userId,
                ppaScopeId,
            );

            try {
                await setDefaultViewMutation.mutateAsync({
                    view_id: view.view_id,
                    is_default: true,
                });
                showViewSetAsDefaultNotification(view.view_name);
            } catch (error) {
                restoreViewListCache(queryClient, scenarioId, previousList, userId, ppaScopeId);
                sendNotification(
                    'error',
                    resolvePpaViewApiErrorMessage(error, 'Failed to set default view. Please try again.'),
                );
            }
        },
        [
            ppaScopeId,
            queryClient,
            scenarioId,
            setDefaultViewMutation,
            showViewSetAsDefaultNotification,
            userId,
        ],
    );

    const onSelectSavedView = (viewId: number) => {
        setExpandedViewId((current) => (current === viewId ? null : viewId));
    };

    const onOpenSaveViewForm = () => {
        resetSaveViewForm();
        setDropdownViewMode('save');
    };

    const onCancelSaveView = () => {
        resetSaveViewForm();
        setDropdownViewMode('list');
    };

    const onSaveView = () => {
        void saveCurrentView();
    };

    const dropdownContent =
        dropdownViewMode === 'save' ? (
            <SavedViewDropdownPanel>
                <SaveCurrentViewForm
                    showEmptyHeader={!savedViews.length}
                    formState={saveViewForm}
                    existingViewNames={savedViews.map((view) => view.view_name)}
                    isSaving={createViewMutation.isPending}
                    onFormChange={setSaveViewForm}
                    onCancel={onCancelSaveView}
                    onSave={onSaveView}
                />
            </SavedViewDropdownPanel>
        ) : (
            <SavedViewDropdownPanel>
                {isViewListLoading ? (
                    <div className="flex justify-center py-6">
                        <Spin size="small" />
                    </div>
                ) : isViewListError ? (
                    <div className="flex flex-col items-center gap-3 px-3 py-4 text-center">
                        <div className="text-[12px] text-[#71717b]">Failed to load saved views.</div>
                        <Button size="small" onClick={() => refetch()}>
                            Retry
                        </Button>
                    </div>
                ) : (
                    <SavedViewsListDropdown
                        savedViews={savedViews}
                        expandedViewId={expandedViewId}
                        activeViewId={activeViewId}
                        activeDataSource={activeDataSource}
                        actionLoading={actionLoading}
                        canSaveCurrentView={
                            Boolean(resolveTemplateViewId()) &&
                            !createViewMutation.isPending &&
                            userCreatedViewsCount < MAX_USER_CREATED_VIEWS
                        }
                        isSaveLimitReached={userCreatedViewsCount >= MAX_USER_CREATED_VIEWS}
                        onSelectSavedView={onSelectSavedView}
                        onOpenSaveViewForm={onOpenSaveViewForm}
                        onLoadView={handleLoadView}
                        onDeleteView={handleDeleteView}
                        onSetDefaultView={handleSetDefaultView}
                        onRenameView={handleRenameView}
                        onUpdateLoadedView={handleUpdateLoadedView}
                        onExitView={handleExitView}
                        isViewModified={isViewModified}
                        showModifiedIndicator={showModifiedIndicator}
                    />
                )}
            </SavedViewDropdownPanel>
        );

    return (
        <Dropdown
            trigger={['click']}
            open={isDropdownOpen}
            onOpenChange={(open) => {
                setIsDropdownOpen(open);
                if (!open) {
                    setExpandedViewId(null);
                    setDropdownViewMode('list');
                    resetSaveViewForm();
                }
            }}
            placement="bottomRight"
            dropdownRender={() => dropdownContent}
            overlayClassName="saved-view-state-popover"
        >
            <Button
                className="toolbar-btn !flex !h-[30px] !w-[140px] !items-center !gap-1 !px-2"
                loading={isViewListLoading && !isDropdownOpen}
            >
                <span className="flex min-w-0 flex-1 overflow-hidden">
                    {showModifiedIndicator ? (
                        <Tooltip {...SAVE_VIEW_TOOLTIP_PROPS} title={UPDATED_VIEW_MODIFIED_TOOLTIP}>
                            <span className="block min-w-0 overflow-hidden">{viewLabelContent}</span>
                        </Tooltip>
                    ) : (
                        viewLabelContent
                    )}
                </span>
                <span className="shrink-0">
                    <DownSvg />
                </span>
            </Button>
        </Dropdown>
    );
};

export default SavedViewStateSelect;
